"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Heart } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useAuth } from '@/contexts/auth-context';
import { useRouter } from 'next/navigation';
import { toggleFavorite, isFavorited as checkIsFavorited } from '@/services/favorites';
import type { FavoriteItem } from '@/services/favorites';
import { trackMediaEvent, type MediaEvent } from '@/services/stats';
import { cn } from '@/lib/utils';

interface SaveButtonProps {
  itemId: string;
  itemType: FavoriteItem['itemType'];
  title: string;
  subtitle?: string;
  imageUrl?: string;
  audioUrl?: string;
  videoUrl?: string;
  youtubeId?: string;
  link?: string;
  size?: 'sm' | 'default' | 'lg' | 'icon';
  variant?: 'default' | 'outline' | 'ghost';
  showLabel?: boolean;
  className?: string;
}

/**
 * Reusable Save/Heart button that persists to Firestore favorites.
 * Requires login — redirects to /signin if not authenticated.
 * Shows filled heart when saved, outline when not.
 */
export default function SaveButton({
  itemId,
  itemType,
  title,
  subtitle,
  imageUrl,
  audioUrl,
  videoUrl,
  youtubeId,
  link,
  size = 'sm',
  variant = 'outline',
  showLabel = true,
  className = '',
}: SaveButtonProps) {
  const { user } = useAuth();
  const tc = useTranslations('common');
  const router = useRouter();
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!user?.uid || !itemId) {
      setIsSaved(false);
      return;
    }
    checkIsFavorited(user.uid, itemId).then(docId => setIsSaved(!!docId));
  }, [user?.uid, itemId]);

  const handleToggle = useCallback(async () => {
    if (!user?.uid) {
      router.push('/signin');
      return;
    }
    if (!itemId) return;
    setIsLoading(true);
    try {
      const result = await toggleFavorite(user.uid, {
        itemId,
        itemType,
        title,
        subtitle: subtitle || '',
        imageUrl: imageUrl || '',
        audioUrl: audioUrl || '',
        videoUrl: videoUrl || '',
        youtubeId: youtubeId || '',
        link: link || '',
      });
      setIsSaved(result.added);
      // Track favorite event (only on add, not remove)
      if (result.added) {
        const entityTypeMap: Record<string, MediaEvent['entityType']> = {
          track: 'track', release: 'release', artist: 'artist', video: 'video',
          news: 'news', event: 'event', playlist: 'playlist', marketplace: 'marketplace',
          radio: 'radio',
        };
        trackMediaEvent({
          type: 'favorite',
          entityType: entityTypeMap[itemType] || 'other',
          entityId: itemId,
          entityTitle: title,
          userId: user.uid,
        });
      }
    } catch (err) {
      console.error('Error toggling save:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.uid, itemId, itemType, title, subtitle, imageUrl, audioUrl, videoUrl, youtubeId, link, router]);

  return (
    <Button
      variant={isSaved ? 'default' : variant}
      size={size}
      className={cn('gap-1.5', className)}
      onClick={handleToggle}
      disabled={isLoading}
      title={isSaved ? tc('removeFromFavorites') : tc('saveToFavorites')}
      aria-label={isSaved ? tc('removeFromFavorites') : tc('saveToFavorites')}
      aria-pressed={isSaved}
    >
      <Heart className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} />
      {showLabel && (isSaved ? tc('savedLabel') : tc('save'))}
    </Button>
  );
}

/**
 * Small icon-only heart button for use in card overlays, grids, etc.
 */
export function SaveIconButton({
  itemId,
  itemType,
  title,
  subtitle,
  imageUrl,
  audioUrl,
  videoUrl,
  youtubeId,
  link,
  className = '',
}: Omit<SaveButtonProps, 'size' | 'variant' | 'showLabel'>) {
  return (
    <SaveButton
      itemId={itemId}
      itemType={itemType}
      title={title}
      subtitle={subtitle}
      imageUrl={imageUrl}
      audioUrl={audioUrl}
      videoUrl={videoUrl}
      youtubeId={youtubeId}
      link={link}
      size="icon"
      variant="ghost"
      showLabel={false}
      className={cn('h-8 w-8 rounded-full', className)}
    />
  );
}
